import { Image, useTheme, View } from '@aws-amplify/ui-react'
import { QueryClient, QueryClientProvider, useQuery } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { App as AntdApp } from 'antd'
import { Amplify } from 'aws-amplify'
import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import duration from 'dayjs/plugin/duration'
import isoWeekPlugin from 'dayjs/plugin/isoWeek'
import relativeTime from 'dayjs/plugin/relativeTime'
import timezone from 'dayjs/plugin/timezone'
import utcPlugin from 'dayjs/plugin/utc'
import React, { lazy, Suspense } from 'react'
import { createRoot } from 'react-dom/client'
import { Provider } from 'react-redux'
import { RouterProvider } from 'react-router-dom'

import $api from './API'
import { awsConfig } from './aws/exports'
import { AuthProvider } from './components/auth/auth-context'
import { AuthListener } from './components/auth/auth-listener'
import { PasskeyAwareAuthenticator } from './components/auth/passkey-aware-authenticator'
import { PasskeySignInFooter } from './components/auth/passkey-sign-in-footer'
import { CommitSetter } from './components/commit-setter'
import { SocketProvider } from './lib/socket'
import { AppVersion } from './components/meta/app-version'
import { Copyright } from './components/meta/copyright'
import { Previewer } from './components/previewer'
import { SentryUserProvider } from './components/sentry/sentry-user-provider'
import * as sentry from './lib/sentry'
import reportWebVitals from './report-web-vitals'
import { router } from './router'
import { store } from './store'

import '@aws-amplify/ui-react/styles.css'
import './index.css'
import('highlight.js/styles/github.css')

Amplify.configure(awsConfig)
// Configure dayjs plugins
dayjs.extend(utcPlugin)
dayjs.extend(timezone)
dayjs.extend(isoWeekPlugin)
dayjs.extend(customParseFormat)
dayjs.extend(duration)
dayjs.extend(relativeTime)

const client = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
      refetchInterval: false
    }
  }
})

const ReactQueryDevtoolsProduction = lazy(async () =>
  import('@tanstack/react-query-devtools/production').then(d => ({
    default: d.ReactQueryDevtools
  }))
)

function App(): React.ReactNode {
  const [showDevtools, setShowDevtools] = React.useState(false)

  React.useEffect(() => {
    const windowWithDevtools = window as Window & { toggleDevtools?: () => void }
    windowWithDevtools.toggleDevtools = () => {
      setShowDevtools(show => !show)
    }
  }, [])

  return (
    <PasskeyAwareAuthenticator
      hideSignUp
      components={{
        Header() {
          const { tokens } = useTheme()

          return (
            <View
              padding={tokens.space.medium}
              backgroundColor='white'
              className='flex flex-col items-center rounded-t-2xl'
            >
              <Image alt='Lucent Health Logo' src='/logo-2.png' width='240px' className='mt-12' />
            </View>
          )
        },
        Footer() {
          return (
            <Provider store={store}>
              <QueryClientProvider client={client}>
                <CommitSetter />

                <Footer />
              </QueryClientProvider>
            </Provider>
          )
        },
        SignIn: {
          Footer: PasskeySignInFooter
        }
      }}
      formFields={{
        signIn: {
          username: {
            labelHidden: true,
            placeholder: 'Email'
          },
          password: {
            labelHidden: true,
            placeholder: 'Password'
          }
        }
      }}
    >
      {({ user, signOut }) => (
        <AuthProvider value={{ user, signOut }}>
          <AuthListener />
          <SocketProvider>
            <Provider store={store}>
              <QueryClientProvider client={client}>
                <SentryUserProvider>
                  <AntdApp>
                    <RouterProvider router={router} />
                  </AntdApp>
                </SentryUserProvider>

                <CommitSetter />
                <Previewer />

                <ReactQueryDevtools
                  position='bottom-right'
                  toggleButtonProps={{
                    className: 'opacity-10 hover:opacity-100'
                  }}
                />

                {showDevtools && (
                  <Suspense fallback={null}>
                    <ReactQueryDevtoolsProduction />
                  </Suspense>
                )}
              </QueryClientProvider>
            </Provider>
          </SocketProvider>
        </AuthProvider>
      )}
    </PasskeyAwareAuthenticator>
  )
}

function Footer(): React.ReactNode {
  useQuery(
    ['metadata'],
    async () => {
      // INFO: We get build-commit-ref and build-commit-date headers from this request
      await $api.get('/metadata')

      return null
    },
    {
      refetchInterval: 60 * 1000
    }
  )

  return (
    <div className='flex flex-col gap-2 items-center'>
      <Copyright className='text-center text-sm text-[#717171] bg-[#E5E9EB] mt-8' />

      <AppVersion commitsDirection='column' />
    </div>
  )
}

const container = document.querySelector('#root')
if (container) {
  const root = createRoot(container)
  root.render(
    <React.StrictMode>
      <App />
    </React.StrictMode>
  )
}

document.querySelector('#env-injector')?.remove()

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals()
sentry.init()
